// ============================================================================
// REPORT EVIDENCE
// ----------------------------------------------------------------------------
// Screenshots attached to player reports so admins have proof, not a claim.
//   captureScreenshot()  : renders the current table view to a JPEG blob
//                          (html2canvas; the report/menu overlays are skipped
//                          so the capture shows what the player saw)
//   fileToEvidenceBlob() : a photo the player picked from their device
//   uploadEvidence()     : stores it under reportEvidence/{uid}/ and returns
//                          the download URL to pass to submitReport()
// Storage rules limit writes to the owner's folder and reads to the owner
// and admin. The server only accepts evidence URLs under the reporter's own
// folder, so nobody can attach somebody else's upload to a report.
//
// Known limit: on iOS native, Agora video tiles are drawn by the OS above the
// web view, so they come out blank in a capture. Chat, seats and names still
// show, and the report carries the reported player's id regardless.
// ============================================================================
import html2canvas from 'html2canvas';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.82;

const canvasToJpeg = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY));

export const captureScreenshot = async (): Promise<Blob | null> => {
  try {
    const target = (document.querySelector('.game-page') as HTMLElement | null) || document.body;
    const width = target.clientWidth || window.innerWidth;
    const canvas = await html2canvas(target, {
      useCORS: true,
      logging: false,
      backgroundColor: '#0b1220',
      scale: Math.min(1, MAX_EDGE / Math.max(width, 1)),
      ignoreElements: (el) =>
        el.classList?.contains('media-actions-backdrop') ||
        el.classList?.contains('chat-report-box') ||
        el.classList?.contains('chat-msg-actions'),
    });
    return await canvasToJpeg(canvas);
  } catch (err) {
    console.warn('[evidence] capture failed:', err);
    return null;
  }
};

/** Downscale and re-encode a picked image so uploads stay small. */
export const fileToEvidenceBlob = (file: File): Promise<Blob | null> =>
  new Promise((resolve) => {
    if (!file.type.startsWith('image/')) { resolve(null); return; }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = async () => {
      const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height, 1));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(await canvasToJpeg(canvas));
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

export const uploadEvidence = async (uid: string, blob: Blob): Promise<string> => {
  const path = `reportEvidence/${uid}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.jpg`;
  const snap = await uploadBytes(ref(storage, path), blob, { contentType: 'image/jpeg' });
  return getDownloadURL(snap.ref);
};

const reportEvidenceService = { captureScreenshot, fileToEvidenceBlob, uploadEvidence };
export default reportEvidenceService;
